/**
 * The forms React Query hooks (forms card 9) over ./formsApi.ts. Every form type shares them:
 * the generic form (components/forms/FormRenderer), the submission page, the forms card on a
 * record and the inbox. A write answers the whole submission, so it goes straight into the
 * submission's cache entry; the lists (mine, on a record, the inbox) are refreshed. A submit or
 * a completion can also mark a Listings task done and write a feed row (the listing adapter),
 * so those refresh the Listings reads too. Forms keep their own timeline, never record history.
 */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { FormItemStatus, FormTargetKind } from '@aiga/app-config/forms';
import { listingsKeys } from '@/components/listings/api/hooks';
import {
  formsApi,
  type CreateFormSubmissionBody,
  type CreateFormTypeBody,
  type FormListFilters,
  type FormSubmission,
  type FormTypeGrantBody,
  type SaveFormSubmissionBody,
  type UpdateFormTypeBody,
} from './formsApi';

export const formsKeys = {
  all: ['forms'] as const,
  catalog: (target?: { kind: FormTargetKind; id: string }) => ['forms', 'catalog', target?.kind ?? null, target?.id ?? null] as const,
  catalogSummary: () => ['forms', 'catalog-summary'] as const,
  submissions: () => ['forms', 'submission'] as const,
  submission: (id: string) => ['forms', 'submission', id] as const,
  files: (id: string) => ['forms', 'files', id] as const,
  mine: () => ['forms', 'mine'] as const,
  onTargets: () => ['forms', 'on-target'] as const,
  onTarget: (kind: FormTargetKind, id: string) => ['forms', 'on-target', kind, id] as const,
  inboxes: () => ['forms', 'inbox'] as const,
  inbox: (filters: FormListFilters) => ['forms', 'inbox', filters] as const,
  manage: () => ['forms', 'manage'] as const,
  draft: (typeId: string) => ['forms', 'draft', typeId] as const,
};

/** The forms that may be started (on `target`, when given: then also the record's label). */
export function useFormsCatalog(target?: { kind: FormTargetKind; id: string }, opts: { enabled?: boolean } = {}) {
  return useQuery({ queryKey: formsKeys.catalog(target), queryFn: () => formsApi.catalog(target), enabled: opts.enabled ?? true });
}

/** The catalog without definitions: enough to decide what to offer (the sidebar's Forms, "Design request"). */
export function useFormsCatalogSummary() {
  return useQuery({ queryKey: formsKeys.catalogSummary(), queryFn: formsApi.catalogSummary });
}

/** One submission; `id` null reads nothing. */
export function useFormSubmission(id: string | null) {
  return useQuery({ queryKey: formsKeys.submission(id ?? ''), queryFn: () => formsApi.get(id as string), enabled: id !== null, retry: false });
}

export function useFormFiles(id: string | null) {
  return useQuery({ queryKey: formsKeys.files(id ?? ''), queryFn: () => formsApi.files(id as string), enabled: id !== null });
}

export function useMyForms() {
  return useQuery({ queryKey: formsKeys.mine(), queryFn: formsApi.mine });
}

export function useFormsOnTarget(kind: FormTargetKind, id: string | null) {
  return useQuery({ queryKey: formsKeys.onTarget(kind, id ?? ''), queryFn: () => formsApi.onTarget(kind, id as string), enabled: id !== null });
}

/** The inbox, a keyset page at a time ("Load more" = fetchNextPage); `limit` sizes each page (the API's default when left out). */
export function useFormsInbox(filters: FormListFilters & { limit?: number }) {
  return useInfiniteQuery({
    queryKey: formsKeys.inbox(filters),
    queryFn: ({ pageParam }) => formsApi.inbox({ ...filters, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
  });
}

/** After a write: the submission as answered, the lists refreshed (and Listings when the target adapter may have reacted). */
function written(qc: QueryClient, s: FormSubmission, opts: { listings?: boolean } = {}) {
  qc.setQueryData(formsKeys.submission(s.id), s);
  return Promise.all([
    qc.invalidateQueries({ queryKey: formsKeys.mine() }),
    qc.invalidateQueries({ queryKey: formsKeys.onTargets() }),
    qc.invalidateQueries({ queryKey: formsKeys.inboxes() }),
    ...(opts.listings ? [qc.invalidateQueries({ queryKey: listingsKeys.all })] : []),
  ]);
}

const NO_RECORD_HISTORY = { skipRecordHistoryInvalidation: true } as const;

/** A write on one submission that answers it. */
function useSubmissionWrite<V>(fn: (vars: V) => Promise<FormSubmission>, opts: { listings?: boolean } = {}) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: (s) => written(qc, s, opts), meta: NO_RECORD_HISTORY });
}

export const useCreateFormSubmission = () => useSubmissionWrite((body: CreateFormSubmissionBody) => formsApi.create(body));
export const useSaveFormSubmission = () => useSubmissionWrite(({ id, body }: { id: string; body: SaveFormSubmissionBody }) => formsApi.save(id, body));
export const useAddFormPart = () =>
  useSubmissionWrite(({ id, partKey, ifRevision }: { id: string; partKey: string; ifRevision?: number }) => formsApi.addPart(id, partKey, ifRevision));
export const useRemoveFormPart = () =>
  useSubmissionWrite(({ id, partKey, ifRevision }: { id: string; partKey: string; ifRevision?: number }) => formsApi.removePart(id, partKey, ifRevision));
export const useAskFormSubmission = () =>
  useSubmissionWrite(({ id, userProfileId }: { id: string; userProfileId: string | null }) => formsApi.ask(id, userProfileId));
export const useSubmitFormSubmission = () =>
  useSubmissionWrite(({ id, ifRevision }: { id: string; ifRevision: number }) => formsApi.submit(id, ifRevision), { listings: true });
export const useReturnFormSubmission = () => useSubmissionWrite(({ id, note }: { id: string; note: string }) => formsApi.sendBack(id, note));
export const useCancelFormSubmission = () =>
  useSubmissionWrite(({ id, reason }: { id: string; reason?: string | null }) => formsApi.cancel(id, reason));
export const useArchiveFormSubmission = () => useSubmissionWrite((id: string) => formsApi.archive(id));
export const useAssignFormSubmission = () =>
  useSubmissionWrite(({ id, userProfileId, ifUnassigned }: { id: string; userProfileId: string | null; ifUnassigned?: boolean }) =>
    formsApi.assign(id, userProfileId, ifUnassigned),
  );
export const useMoveFormStage = () => useSubmissionWrite(({ id, stageKey }: { id: string; stageKey: string | null }) => formsApi.moveStage(id, stageKey));
/** The last part finished completes the submission, so the Listings reads refresh too. */
export const useMoveFormPart = () =>
  useSubmissionWrite(
    ({ id, partKey, body }: { id: string; partKey: string; body: { status?: FormItemStatus; stage_key?: string | null } }) => formsApi.movePart(id, partKey, body),
    { listings: true },
  );
export const useCompleteFormSubmission = () => useSubmissionWrite((id: string) => formsApi.complete(id), { listings: true });

/** A file write: the submission's files refreshed (files don't change its revision). */
function useFileWrite<V extends { id: string }, R>(fn: (vars: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (_r, { id }) =>
      Promise.all([qc.invalidateQueries({ queryKey: formsKeys.files(id) }), qc.invalidateQueries({ queryKey: formsKeys.submission(id) })]),
    meta: NO_RECORD_HISTORY,
  });
}

export const useUploadFormFile = () => useFileWrite(({ id, fieldKey, file }: { id: string; fieldKey: string; file: File }) => formsApi.uploadFile(id, fieldKey, file));
export const useRemoveFormFile = () => useFileWrite(({ id, fileId }: { id: string; fileId: string }) => formsApi.removeFile(id, fileId));
export const useArchiveFormFile = () => useFileWrite(({ id, fileId }: { id: string; fileId: string }) => formsApi.archiveFile(id, fileId));

// ── Settings → Forms (card 13) ──

/** The types the caller manages (MANAGE, or every type for an admin), and the roles and departments to pick from. */
export function useFormsManage(opts: { enabled?: boolean } = {}) {
  return useQuery({ queryKey: formsKeys.manage(), queryFn: formsApi.manage, enabled: opts.enabled ?? true });
}

/**
 * A Settings write: a type's catalog row, grants, rules and versions decide what the catalog, the
 * inbox and every form page show, so everything under 'forms' refreshes.
 */
function useFormTypeWrite<V, R>(fn: (vars: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => qc.invalidateQueries({ queryKey: formsKeys.all }), meta: NO_RECORD_HISTORY });
}

export const useCreateFormType = () => useFormTypeWrite((body: CreateFormTypeBody) => formsApi.createType(body));
export const useUpdateFormType = () => useFormTypeWrite(({ id, body }: { id: string; body: UpdateFormTypeBody }) => formsApi.updateType(id, body));
export const useDeleteFormType = () => useFormTypeWrite((id: string) => formsApi.deleteType(id));
export const useAddFormTypeGrant = () => useFormTypeWrite(({ id, body }: { id: string; body: FormTypeGrantBody }) => formsApi.addGrant(id, body));
export const useRemoveFormTypeGrant = () => useFormTypeWrite(({ id, grantId }: { id: string; grantId: string }) => formsApi.removeGrant(id, grantId));
export const usePublishFormTypeVersion = () =>
  useFormTypeWrite(({ id, definition, ifCurrentVersion }: { id: string; definition: unknown; ifCurrentVersion: number | null }) =>
    formsApi.publishVersion(id, definition, ifCurrentVersion),
  );
/** A type's builder draft (null = none); `typeId` null reads nothing. */
export function useFormTypeDraft(typeId: string | null) {
  return useQuery({ queryKey: formsKeys.draft(typeId ?? ''), queryFn: () => formsApi.draft(typeId as string), enabled: typeId !== null });
}

/** A draft write changes only the draft and Settings' label of it: no catalog or inbox shows a draft. */
export function useSaveFormTypeDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, definition, ifUpdatedAt }: { id: string; definition: unknown; ifUpdatedAt?: string | null }) => formsApi.saveDraft(id, definition, ifUpdatedAt),
    onSuccess: (draft, { id }) => {
      qc.setQueryData(formsKeys.draft(id), draft);
      return qc.invalidateQueries({ queryKey: formsKeys.manage() });
    },
    meta: NO_RECORD_HISTORY,
  });
}

export function useDiscardFormTypeDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => formsApi.discardDraft(id),
    onSuccess: (_r, id) => {
      qc.setQueryData(formsKeys.draft(id), null);
      return qc.invalidateQueries({ queryKey: formsKeys.manage() });
    },
    meta: NO_RECORD_HISTORY,
  });
}

/** The check writes nothing: a mutation only because it is a POST the screen runs on demand. */
export const useCheckFormTypeVersion = () =>
  useMutation({ mutationFn: ({ id, definition }: { id: string; definition: unknown }) => formsApi.checkVersion(id, definition), meta: NO_RECORD_HISTORY });
